import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Format currency
//
// Defensa anti-`$NaN`: Intl.NumberFormat.format(NaN) devuelve literalmente
// la cadena `"$NaN"` y ensucia toda la UI cuando llega un valor numérico
// mal formado (string no parseable, undefined coerce, etc). Normalizamos a 0
// y dejamos un único punto de control en lugar de envolver cada caller.
export function formatCurrency(amount: number | string | null | undefined, currency = "MXN"): string {
  let n: number
  if (amount == null) n = 0
  else if (typeof amount === "number") n = amount
  else n = Number(amount)
  if (!Number.isFinite(n)) n = 0
  return new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(n)
}

/**
 * Cifra del precio SIN símbolo, para las tarjetas que ya pintan el "$" y el
 * "MXN" por separado: sin decimales si el precio es entero ("199"), con
 * centavos si los tiene ("124.99").
 *
 * Las tarjetas usaban `price.toFixed(0)`, que REDONDEA: la oferta de $124.99
 * se le mostraba al distribuidor como "$125" y con ese precio cotizaba. El
 * precio que se pinta tiene que ser el que se cobra.
 */
export function precioCifra(amount: number | string | null | undefined): string {
  let n: number
  if (amount == null) n = 0
  else if (typeof amount === "number") n = amount
  else n = Number(String(amount).replace(/,/g, ""))
  if (!Number.isFinite(n)) n = 0
  return Number.isInteger(n) ? n.toFixed(0) : n.toFixed(2)
}

// La BD almacena TIMESTAMP WITHOUT TIME ZONE en UTC (datetime.utcnow()).
// Cuando llega al frontend como "2026-05-07T15:19:24" sin "Z", el Date()
// nativo lo interpreta como hora local del navegador → 6h adelantadas en MX.
// Este helper agrega la "Z" si falta para forzar UTC.
function parseAsUtc(value: string | Date): Date {
  if (value instanceof Date) return value
  // Si ya tiene tz info (Z o +/-HH:MM al final), respetar.
  if (/Z$|[+-]\d{2}:?\d{2}$/.test(value)) return new Date(value)
  return new Date(value + "Z")
}

// Format date
export function formatDate(date: string | Date, options?: Intl.DateTimeFormatOptions): string {
  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "America/Mexico_City",
    ...options,
  }
  return new Intl.DateTimeFormat("es-MX", defaultOptions).format(parseAsUtc(date))
}

// Format date and time
export function formatDateTime(date: string | Date): string {
  return new Intl.DateTimeFormat("es-MX", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "America/Mexico_City",
  }).format(parseAsUtc(date))
}

// Format date only (no time)
export function formatDateOnly(date: string | Date): string {
  return new Intl.DateTimeFormat("es-MX", {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "America/Mexico_City",
  }).format(parseAsUtc(date))
}

// Format time only (HH:MM)
export function formatTime(date: string | Date): string {
  return new Intl.DateTimeFormat("es-MX", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "America/Mexico_City",
  }).format(parseAsUtc(date))
}

// Format relative time
export function formatRelativeTime(date: string | Date): string {
  const now = new Date()
  const then = parseAsUtc(date)
  const diffInSeconds = Math.floor((now.getTime() - then.getTime()) / 1000)

  if (diffInSeconds < 60) return "Hace un momento"
  if (diffInSeconds < 3600) return `Hace ${Math.floor(diffInSeconds / 60)} minutos`
  if (diffInSeconds < 86400) return `Hace ${Math.floor(diffInSeconds / 3600)} horas`
  if (diffInSeconds < 604800) return `Hace ${Math.floor(diffInSeconds / 86400)} días`
  
  return formatDate(date)
}

// Truncate text
export function truncate(text: string, length: number): string {
  if (text.length <= length) return text
  return text.slice(0, length) + "..."
}

// Generate initials from name
export function getInitials(name: string): string {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2)
}

// Debounce function
export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null

  return function (...args: Parameters<T>) {
    if (timeout) clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), wait)
  }
}

// Sleep function
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

// Generate random ID
export function generateId(): string {
  return Math.random().toString(36).substring(2, 9)
}
